/**
 * @module services/pharmacy-duty-view
 * @description UC-REG-296 — Lógica de presentación de las farmacias de
 *   guardia (sin red ni componentes, testeable): aviso fijo de verificación
 *   telefónica, fechas, etiquetas de estado y mensajes de error.
 */

/**
 * @type {string} Aviso fijo que pide el Municipio: la nómina es informativa y
 *   hay que confirmar por teléfono antes de ir.
 */
export const PHARMACY_DUTY_NOTICE =
  "Antes de ir, verificá por teléfono que la farmacia esté abierta. La nómina puede cambiar sin aviso.";

/** @type {Object<string, string>} Códigos de error de `/pharmacy-duty` → mensaje. */
export const PHARMACY_DUTY_ERROR_MESSAGES = {
  InvalidDate: "La fecha no es válida.",
  MissingEntries: "Elegí al menos una farmacia o publicá la nómina vacía.",
  UnknownPharmacy:
    "Alguna de las farmacias elegidas ya no existe en el maestro. Actualizá la lista.",
  AdminOnly: "Solo un administrador puede publicar la nómina.",
  DutyNotFound: "No hay nómina publicada para ese día.",
};

const WEEKDAYS = [
  "domingo",
  "lunes",
  "martes",
  "miércoles",
  "jueves",
  "viernes",
  "sábado",
];
const MONTHS = [
  "enero",
  "febrero",
  "marzo",
  "abril",
  "mayo",
  "junio",
  "julio",
  "agosto",
  "septiembre",
  "octubre",
  "noviembre",
  "diciembre",
];

/**
 * @description Parsea `AAAA-MM-DD` como fecha local (sin corrimiento de zona).
 * @param {string} date
 * @returns {Date|null}
 */
export function parseDutyDate(date) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date || "");
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * @description Formatea una fecha local como `AAAA-MM-DD`.
 * @param {Date} d
 * @returns {string}
 */
export function toDutyDate(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * @description Día de hoy del navegador como `AAAA-MM-DD`.
 * @returns {string}
 */
export function todayDutyDate() {
  return toDutyDate(new Date());
}

/**
 * @description Suma días a un `AAAA-MM-DD`.
 * @param {string} date
 * @param {number} days
 * @returns {string}
 */
export function addDutyDays(date, days) {
  const d = parseDutyDate(date) || new Date();
  d.setDate(d.getDate() + days);
  return toDutyDate(d);
}

/**
 * @description "Miércoles 28 de octubre" (con "Hoy" / "Mañana" si corresponde).
 * @param {string} date - `AAAA-MM-DD`
 * @param {string} [today] - `AAAA-MM-DD` de referencia (default hoy)
 * @returns {string}
 */
export function formatDutyDate(date, today = todayDutyDate()) {
  const d = parseDutyDate(date);
  if (!d) return "";
  const label = `${WEEKDAYS[d.getDay()]} ${d.getDate()} de ${
    MONTHS[d.getMonth()]
  }`;
  const capitalized = label.charAt(0).toUpperCase() + label.slice(1);
  if (date === today) return `Hoy, ${capitalized}`;
  if (date === addDutyDays(today, 1)) return `Mañana, ${capitalized}`;
  if (date === addDutyDays(today, -1)) return `Ayer, ${capitalized}`;
  return capitalized;
}

/**
 * @description Etiqueta de estado de una entrada de la nómina.
 * @param {{onDuty: boolean, hours: string|null}} entry
 * @returns {string}
 */
export function entryStatusLabel(entry) {
  if (!entry) return "";
  if (entry.onDuty)
    return entry.hours ? `De turno · ${entry.hours}` : "De turno (24 h)";
  return entry.hours ? `Abierta · ${entry.hours}` : "Abierta";
}

/**
 * @description `tel:` para el teléfono de la farmacia (solo dígitos y +).
 * @param {string|null} phone
 * @returns {string|null}
 */
export function telHref(phone) {
  if (!phone) return null;
  const clean = String(phone).replace(/[^0-9+]/g, "");
  return clean ? `tel:${clean}` : null;
}

/**
 * @description Mensaje para el usuario a partir de un error de axios de
 *   `/pharmacy-duty`. `undefined` si no hay código conocido (mensaje genérico).
 * @param {Object} err
 * @returns {string|undefined}
 */
export function pharmacyDutyErrorMessage(err) {
  const code =
    err && err.response && err.response.data && err.response.data.error;
  return (code && PHARMACY_DUTY_ERROR_MESSAGES[code]) || undefined;
}
